import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdesaoService } from '../../../../services/adesao/adesao.service';
import { AtendimentoService } from '../../../../services/atendimento/atendimento.service';
import { PacienteCadastrado } from '../../../../services/patient/patient.service';
import { PacienteBusca } from '../../../../interfaces/dados-pagamento';
import { SituacaoVendaCartao } from '../../../../interfaces/adesao.dto';
import { AcaoVendaCartao, acaoDaSituacao, mascararCpf, normalizarCpf, pacienteDoCpf } from '../../../../utils/venda-cartao.util';
import { OfertaSimComponent } from '../../abrir-atendimento/oferta-sim/oferta-sim.component';
import { MelhorarPlanoSimComponent } from '../../abrir-atendimento/melhorar-plano-sim/melhorar-plano-sim.component';
import { CadastroPacienteModalComponent } from '../../../shared/cadastro-paciente-modal/cadastro-paciente-modal.component';
import { PendenciaSimComponent } from '../../abrir-atendimento/pendencia-sim/pendencia-sim.component';
import { PendenciaCanceladaSimComponent } from '../../abrir-atendimento/pendencia-cancelada-sim/pendencia-cancelada-sim.component';
import { BloqueioAdesaoSimComponent } from '../../abrir-atendimento/bloqueio-adesao-sim/bloqueio-adesao-sim.component';

type Tela = 'busca' | 'resultado' | 'oferta';

const ERRO_CONSULTA = 'Não foi possível consultar o cliente. Tente de novo.';

/**
 * Coluna da pausa "Venda Cartão": oferta SiM+ para quem não está em atendimento.
 * A abordagem conta quando a oferta abre; buscar CPF não conta.
 */
@Component({
  selector: 'app-venda-cartao-pausa',
  standalone: true,
  imports: [CommonModule, FormsModule, OfertaSimComponent, MelhorarPlanoSimComponent, CadastroPacienteModalComponent,
            PendenciaSimComponent, PendenciaCanceladaSimComponent, BloqueioAdesaoSimComponent],
  templateUrl: './venda-cartao-pausa.component.html',
  styles: [`
    .vc-titulo { font-size: 18px; font-weight: 700; margin-top: 2px; color: var(--tx); }
    .vc-sub { font-size: 12px; color: var(--tx-2); margin-top: 3px; }
    .vc-cartao { background: var(--cartao); border: 1px solid var(--flua-borda); border-radius: 14px; padding: 14px 16px; }
    .vc-btn { height: 42px; padding: 0 18px; border-radius: 10px; font-size: 13px; font-weight: 700; white-space: nowrap; }
    .vc-btn:disabled { opacity: .5; cursor: not-allowed; }
    .vc-btn.prim { color: #fff; background: var(--brand-primary); }
    .vc-btn.sec { color: var(--tx-2); background: var(--cartao); border: 1px solid var(--flua-borda); }
    .vc-avatar { display: grid; place-items: center; width: 36px; height: 36px; flex-shrink: 0; border-radius: 999px;
                 color: #fff; background: var(--brand-primary); font-size: 12px; font-weight: 700; }
    .vc-chip { padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; white-space: nowrap;
               color: var(--chip-tx); background: var(--chip-bg); }
    .vc-chip.verde { color: var(--brand-primary); background: var(--brand-tint); }
    .vc-chip.ambar { color: #8A5A0C; background: #FDF3E2; }
    :host-context(.dark) .vc-chip.ambar { color: #F3C77A; background: rgba(243, 199, 122, .14); }
  `],
})
export class VendaCartaoPausaComponent {
  @Input() clinicId = 0;
  @Input() guiche = '';
  @Output() abordagemEncerrada = new EventEmitter<void>();

  tela: Tela = 'busca';
  cpfDigitado = '';
  cpf = '';
  buscando = false;
  erro = '';
  sucesso = '';
  paciente: PacienteBusca | null = null;
  situacao: SituacaoVendaCartao | null = null;
  acao: AcaoVendaCartao | null = null;
  showCadastro = false;
  vendido = false;
  // null enquanto o card ainda consulta; a oferta de adesão só abre com os dois em false.
  temCancelada: boolean | null = null;
  temBloqueio: boolean | null = null;
  temPendenciaAtraso: boolean | null = null;
  regularizou = false;

  constructor(private adesaoService: AdesaoService, private atendimentoService: AtendimentoService) {}

  get cpfMascarado(): string {
    return mascararCpf(this.cpf);
  }

  get iniciais(): string {
    const partes = (this.paciente?.nmPessoaFisica ?? '').trim().split(/\s+/).filter(Boolean);
    return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
  }

  get textoOfertaAnterior(): string {
    const anterior = this.situacao?.ofertaAnterior;
    if (!anterior) return '';
    const familia = anterior.familia ? anterior.familia[0].toUpperCase() + anterior.familia.slice(1) : '';
    const [, mes, dia] = /^\d{4}-(\d{2})-(\d{2})/.exec(anterior.data ?? '') ?? [];
    const quando = dia ? ` de ${dia}/${mes}` : '';
    return `Recusou o ${familia} na abertura${quando} (por ${anterior.operador})`;
  }

  /** Fatura em aberto de assinatura cancelada (ou multa/anuidade) faz o CartãoSiM recusar a adesão no fim. */
  get adesaoBloqueada(): boolean {
    return this.temCancelada === true || this.temBloqueio === true;
  }

  get verificandoAdesao(): boolean {
    return !this.adesaoBloqueada && (this.temCancelada === null || this.temBloqueio === null);
  }

  get adesaoLiberada(): boolean {
    return this.temCancelada === false && this.temBloqueio === false;
  }

  async buscar(): Promise<void> {
    if (this.buscando) return;
    this.erro = '';
    this.sucesso = '';
    const cpf = normalizarCpf(this.cpfDigitado);
    if (!cpf) {
      this.erro = 'Digite os 11 números do CPF.';
      return;
    }
    this.buscando = true;
    try {
      const lista = await this.atendimentoService.buscarPaciente(cpf);
      this.cpf = cpf;
      const paciente = pacienteDoCpf(lista, cpf);
      if (paciente) {
        await this.consultarSituacao(paciente);
      } else {
        this.zerarVerificacoes();
        this.paciente = null;
        this.situacao = null;
        this.acao = null;
        this.tela = 'resultado';
        this.cpfDigitado = '';
      }
    } catch {
      this.erro = ERRO_CONSULTA;
    } finally {
      this.buscando = false;
    }
  }

  private async consultarSituacao(paciente: PacienteBusca): Promise<void> {
    const situacao = await this.adesaoService.situacaoVendaCartao(this.clinicId, paciente.cdPessoaFisica, this.cpf);
    if (!situacao) {
      this.erro = ERRO_CONSULTA;
      this.tela = 'busca';
      return;
    }
    this.zerarVerificacoes();
    this.paciente = paciente;
    this.situacao = situacao;
    this.acao = acaoDaSituacao(situacao.situacao);
    this.tela = 'resultado';
    this.cpfDigitado = '';
  }

  ofertar(): void {
    if (!this.paciente || (this.acao !== 'adesao' && this.acao !== 'upsell')) return;
    if (this.acao === 'adesao' && !this.adesaoLiberada) return;
    this.adesaoService.registrarAbordagem(this.clinicId, this.paciente.cdPessoaFisica, this.acao);
    this.tela = 'oferta';
  }

  trocarCpf(): void {
    const encerrou = this.tela === 'oferta';
    this.voltarParaBusca();
    if (encerrou) this.abordagemEncerrada.emit();
  }

  // Fica na oferta: a tela de resultado do filho avisa dependente que ficou de fora e assinatura antiga não cancelada.
  onVendido(): void {
    this.vendido = true;
    this.sucesso = 'Venda registrada no seu nome';
  }

  // Quitou a cancelada: o card de bloqueio volta a consultar, então o resultado antigo não vale.
  onTemCancelada(tem: boolean): void {
    if (this.temCancelada === true && !tem) this.temBloqueio = null;
    this.temCancelada = tem;
  }

  /** Pagou a pendência na pausa: a situação muda (em atraso vira cliente) e pode seguir para o melhorar plano. */
  async onRegularizado(): Promise<void> {
    if (!this.paciente) return;
    await this.consultarSituacao(this.paciente);
    this.regularizou = true;
  }

  async onCadastrado(p: PacienteCadastrado): Promise<void> {
    this.showCadastro = false;
    this.erro = '';
    this.cpf = normalizarCpf(p.cpf) ?? this.cpf;
    await this.consultarSituacao({ cdPessoaFisica: p.cdPessoaFisica, nmPessoaFisica: p.nome, nrCpf: this.cpf });
  }

  private voltarParaBusca(): void {
    this.tela = 'busca';
    this.cpfDigitado = '';
    this.cpf = '';
    this.paciente = null;
    this.situacao = null;
    this.acao = null;
    this.erro = '';
    this.sucesso = '';
    this.vendido = false;
    this.zerarVerificacoes();
  }

  private zerarVerificacoes(): void {
    this.temCancelada = null;
    this.temBloqueio = null;
    this.temPendenciaAtraso = null;
    this.regularizou = false;
  }
}
